import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  HStack,
  Box,
  Text,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useToast,
  Spinner,
  Divider,
  Badge,
  IconButton,
  Flex,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import offerService from "../services/offerService";
import atsService from "../services/atsService";

const CreateOfferModal = ({
  isOpen,
  onClose,
  onSuccess,
  preSelectedApplication,
}) => {
  const [loading, setLoading] = useState(false);
  const [applications, setApplications] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [formData, setFormData] = useState({
    applicationId: "",
    templateId: "",
    validTill: "",
    offerDetails: {
      designation: "",
      department: "",
      location: "",
      employmentType: "Full-time",
      jobLevel: "Mid Level",
      joiningDate: "",
      reportingManager: {
        name: "",
        designation: "",
        email: "",
      },
      ctc: "",
      currency: "INR",
      salaryBreakdown: {
        basic: "",
        hra: "",
        conveyance: "",
        lta: "",
        medical: "",
        otherAllowances: "",
      },
      benefits: [],
      probationPeriod: {
        duration: 3,
      },
      noticePeriod: 1,
      workLocation: "On-site",
    },
    approvals: [],
  });

  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      fetchApplications();
      fetchTemplates();
    }
  }, [isOpen]);

  useEffect(() => {
    if (preSelectedApplication) {
      // Pre-select the application and populate form
      setSelectedApplication(preSelectedApplication._id);
      setFormData((prev) => ({
        ...prev,
        applicationId: preSelectedApplication._id,
        offerDetails: {
          ...prev.offerDetails,
          designation:
            preSelectedApplication.requisitionId?.jobTitle ||
            preSelectedApplication.requisitionId?.positionTitle ||
            "",
          department: preSelectedApplication.requisitionId?.department || "",
          location: preSelectedApplication.requisitionId?.location || "",
          employmentType:
            preSelectedApplication.requisitionId?.employmentType || "Full-time",
        },
      }));
    }
  }, [preSelectedApplication]);

  useEffect(() => {
    if (selectedApplication) {
      // Auto-fill form data from selected application
      const app = applications.find((a) => a._id === selectedApplication);
      if (app) {
        setFormData((prev) => ({
          ...prev,
          offerDetails: {
            ...prev.offerDetails,
            designation: app.requisitionId?.jobTitle || "",
            department: app.requisitionId?.department || "",
            location: app.requisitionId?.location || "",
          },
        }));
      }
    }
  }, [selectedApplication, applications]);

  const fetchApplications = async () => {
    try {
      const response = await atsService.getApplications({ status: "selected" });
      setApplications(response.data.data || []);
    } catch (error) {
      console.error("Error fetching applications:", error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await offerService.getTemplates();
      setTemplates(response.data.data || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const handleApplicationChange = (applicationId) => {
    setSelectedApplication(applicationId);
    setFormData((prev) => ({
      ...prev,
      applicationId,
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleOfferDetailsChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      offerDetails: {
        ...prev.offerDetails,
        [field]: value,
      },
    }));
  };

  const handleSalaryBreakdownChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      offerDetails: {
        ...prev.offerDetails,
        salaryBreakdown: {
          ...prev.offerDetails.salaryBreakdown,
          [field]: value,
        },
      },
    }));
  };

  const handleReportingManagerChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      offerDetails: {
        ...prev.offerDetails,
        reportingManager: {
          ...prev.offerDetails.reportingManager,
          [field]: value,
        },
      },
    }));
  };

  const addBenefit = () => {
    setFormData((prev) => ({
      ...prev,
      offerDetails: {
        ...prev.offerDetails,
        benefits: [...prev.offerDetails.benefits, ""],
      },
    }));
  };

  const updateBenefit = (index, value) => {
    setFormData((prev) => ({
      ...prev,
      offerDetails: {
        ...prev.offerDetails,
        benefits: prev.offerDetails.benefits.map((benefit, i) =>
          i === index ? value : benefit
        ),
      },
    }));
  };

  const removeBenefit = (index) => {
    setFormData((prev) => ({
      ...prev,
      offerDetails: {
        ...prev.offerDetails,
        benefits: prev.offerDetails.benefits.filter((_, i) => i !== index),
      },
    }));
  };

  const addApproval = () => {
    setFormData((prev) => ({
      ...prev,
      approvals: [
        ...prev.approvals,
        {
          approverId: "",
          approverName: "",
          approverRole: "",
          status: "pending",
          approvalLevel: prev.approvals.length + 1,
        },
      ],
    }));
  };

  const updateApproval = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      approvals: prev.approvals.map((approval, i) =>
        i === index ? { ...approval, [field]: value } : approval
      ),
    }));
  };

  const removeApproval = (index) => {
    setFormData((prev) => ({
      ...prev,
      approvals: prev.approvals.filter((_, i) => i !== index),
    }));
  };

  const calculateTotalCTC = () => {
    const breakdown = formData.offerDetails.salaryBreakdown;
    return (
      (parseFloat(breakdown.basic) || 0) +
      (parseFloat(breakdown.hra) || 0) +
      (parseFloat(breakdown.conveyance) || 0) +
      (parseFloat(breakdown.lta) || 0) +
      (parseFloat(breakdown.medical) || 0) +
      (parseFloat(breakdown.otherAllowances) || 0)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      // Calculate total CTC if not provided
      const totalCTC = formData.offerDetails.ctc || calculateTotalCTC();

      const offerData = {
        ...formData,
        offerDetails: {
          ...formData.offerDetails,
          ctc: totalCTC,
          benefits: formData.offerDetails.benefits.filter((b) => b.trim()),
        },
      };

      await offerService.createOffer(offerData);

      toast({
        title: "Success",
        description: "Offer created successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      onSuccess();
    } catch (error) {
      console.error("Error creating offer:", error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to create offer",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create New Offer</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={6} align="stretch">
              {/* Basic Information */}
              <Box>
                <Text fontSize="lg" fontWeight="bold" mb={4}>
                  Basic Information
                </Text>
                <VStack spacing={4} align="stretch">
                  <HStack spacing={4}>
                    <FormControl isRequired>
                      <FormLabel>Application</FormLabel>
                      <Select
                        placeholder="Select application"
                        value={formData.applicationId}
                        onChange={(e) =>
                          handleApplicationChange(e.target.value)
                        }
                      >
                        {applications.map((app) => (
                          <option key={app._id} value={app._id}>
                            {app.candidateId?.name} -{" "}
                            {app.requisitionId?.jobTitle}
                          </option>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>Template</FormLabel>
                      <Select
                        placeholder="Select template"
                        value={formData.templateId}
                        onChange={(e) =>
                          handleInputChange("templateId", e.target.value)
                        }
                      >
                        {templates.map((template) => (
                          <option key={template._id} value={template._id}>
                            {template.name}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </HStack>

                  <FormControl isRequired>
                    <FormLabel>Valid Till</FormLabel>
                    <Input
                      type="date"
                      value={formData.validTill}
                      onChange={(e) =>
                        handleInputChange("validTill", e.target.value)
                      }
                    />
                  </FormControl>
                </VStack>
              </Box>

              <Divider />

              {/* Position Details */}
              <Box>
                <Text fontSize="lg" fontWeight="bold" mb={4}>
                  Position Details
                </Text>
                <VStack spacing={4} align="stretch">
                  <HStack spacing={4}>
                    <FormControl isRequired>
                      <FormLabel>Designation</FormLabel>
                      <Input
                        value={formData.offerDetails.designation}
                        onChange={(e) =>
                          handleOfferDetailsChange(
                            "designation",
                            e.target.value
                          )
                        }
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>Department</FormLabel>
                      <Input
                        value={formData.offerDetails.department}
                        onChange={(e) =>
                          handleOfferDetailsChange("department", e.target.value)
                        }
                      />
                    </FormControl>
                  </HStack>

                  <HStack spacing={4}>
                    <FormControl isRequired>
                      <FormLabel>Location</FormLabel>
                      <Input
                        value={formData.offerDetails.location}
                        onChange={(e) =>
                          handleOfferDetailsChange("location", e.target.value)
                        }
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>Employment Type</FormLabel>
                      <Select
                        value={formData.offerDetails.employmentType}
                        onChange={(e) =>
                          handleOfferDetailsChange(
                            "employmentType",
                            e.target.value
                          )
                        }
                      >
                        <option value="Full-time">Full-time</option>
                        <option value="Part-time">Part-time</option>
                        <option value="Contract">Contract</option>
                        <option value="Internship">Internship</option>
                        <option value="Freelance">Freelance</option>
                      </Select>
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>Job Level</FormLabel>
                      <Select
                        value={formData.offerDetails.jobLevel}
                        onChange={(e) =>
                          handleOfferDetailsChange("jobLevel", e.target.value)
                        }
                      >
                        <option value="Entry Level">Entry Level</option>
                        <option value="Mid Level">Mid Level</option>
                        <option value="Senior Level">Senior Level</option>
                        <option value="Executive">Executive</option>
                        <option value="Management">Management</option>
                      </Select>
                    </FormControl>
                  </HStack>

                  <HStack spacing={4}>
                    <FormControl isRequired>
                      <FormLabel>Joining Date</FormLabel>
                      <Input
                        type="date"
                        value={formData.offerDetails.joiningDate}
                        onChange={(e) =>
                          handleOfferDetailsChange(
                            "joiningDate",
                            e.target.value
                          )
                        }
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>Work Location</FormLabel>
                      <Select
                        value={formData.offerDetails.workLocation}
                        onChange={(e) =>
                          handleOfferDetailsChange(
                            "workLocation",
                            e.target.value
                          )
                        }
                      >
                        <option value="On-site">On-site</option>
                        <option value="Remote">Remote</option>
                        <option value="Hybrid">Hybrid</option>
                      </Select>
                    </FormControl>
                  </HStack>
                </VStack>
              </Box>

              <Divider />

              {/* Reporting Manager */}
              <Box>
                <Text fontSize="lg" fontWeight="bold" mb={4}>
                  Reporting Manager
                </Text>
                <HStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Name</FormLabel>
                    <Input
                      value={formData.offerDetails.reportingManager.name}
                      onChange={(e) =>
                        handleReportingManagerChange("name", e.target.value)
                      }
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Designation</FormLabel>
                    <Input
                      value={formData.offerDetails.reportingManager.designation}
                      onChange={(e) =>
                        handleReportingManagerChange(
                          "designation",
                          e.target.value
                        )
                      }
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Email</FormLabel>
                    <Input
                      type="email"
                      value={formData.offerDetails.reportingManager.email}
                      onChange={(e) =>
                        handleReportingManagerChange("email", e.target.value)
                      }
                    />
                  </FormControl>
                </HStack>
              </Box>

              <Divider />

              {/* Compensation */}
              <Box>
                <Text fontSize="lg" fontWeight="bold" mb={4}>
                  Compensation Details
                </Text>
                <VStack spacing={4} align="stretch">
                  <HStack spacing={4}>
                    <FormControl>
                      <FormLabel>Currency</FormLabel>
                      <Select
                        value={formData.offerDetails.currency}
                        onChange={(e) =>
                          handleOfferDetailsChange("currency", e.target.value)
                        }
                      >
                        <option value="INR">INR</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                      </Select>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Total CTC (Auto-calculated)</FormLabel>
                      <Input
                        value={calculateTotalCTC().toLocaleString()}
                        isReadOnly
                      />
                    </FormControl>
                  </HStack>

                  <Text fontWeight="bold">Monthly Salary Breakdown:</Text>
                  <HStack spacing={4}>
                    <FormControl>
                      <FormLabel>Basic Salary</FormLabel>
                      <NumberInput
                        value={formData.offerDetails.salaryBreakdown.basic}
                        onChange={(value) =>
                          handleSalaryBreakdownChange("basic", value)
                        }
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>

                    <FormControl>
                      <FormLabel>HRA</FormLabel>
                      <NumberInput
                        value={formData.offerDetails.salaryBreakdown.hra}
                        onChange={(value) =>
                          handleSalaryBreakdownChange("hra", value)
                        }
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Conveyance</FormLabel>
                      <NumberInput
                        value={formData.offerDetails.salaryBreakdown.conveyance}
                        onChange={(value) =>
                          handleSalaryBreakdownChange("conveyance", value)
                        }
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                  </HStack>

                  <HStack spacing={4}>
                    <FormControl>
                      <FormLabel>LTA</FormLabel>
                      <NumberInput
                        value={formData.offerDetails.salaryBreakdown.lta}
                        onChange={(value) =>
                          handleSalaryBreakdownChange("lta", value)
                        }
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Medical Allowance</FormLabel>
                      <NumberInput
                        value={formData.offerDetails.salaryBreakdown.medical}
                        onChange={(value) =>
                          handleSalaryBreakdownChange("medical", value)
                        }
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Other Allowances</FormLabel>
                      <NumberInput
                        value={
                          formData.offerDetails.salaryBreakdown.otherAllowances
                        }
                        onChange={(value) =>
                          handleSalaryBreakdownChange("otherAllowances", value)
                        }
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                  </HStack>
                </VStack>
              </Box>

              <Divider />

              {/* Benefits */}
              <Box>
                <Text fontSize="lg" fontWeight="bold" mb={4}>
                  Benefits & Perks
                </Text>
                <VStack spacing={2} align="stretch">
                  {formData.offerDetails.benefits.map((benefit, index) => (
                    <HStack key={index} spacing={2}>
                      <Input
                        placeholder="Enter benefit"
                        value={benefit}
                        onChange={(e) => updateBenefit(index, e.target.value)}
                      />
                      <IconButton
                        icon={<DeleteIcon />}
                        colorScheme="red"
                        variant="outline"
                        onClick={() => removeBenefit(index)}
                      />
                    </HStack>
                  ))}
                  <Button
                    leftIcon={<AddIcon />}
                    variant="outline"
                    onClick={addBenefit}
                    alignSelf="flex-start"
                  >
                    Add Benefit
                  </Button>
                </VStack>
              </Box>

              <Divider />

              {/* Approvals */}
              <Box>
                <Text fontSize="lg" fontWeight="bold" mb={4}>
                  Approval Workflow
                </Text>
                <VStack spacing={2} align="stretch">
                  {formData.approvals.map((approval, index) => (
                    <HStack
                      key={index}
                      spacing={2}
                      p={3}
                      borderWidth={1}
                      borderRadius="md"
                    >
                      <Badge colorScheme="blue">
                        Level {approval.approvalLevel}
                      </Badge>
                      <Input
                        placeholder="Approver Name"
                        value={approval.approverName}
                        onChange={(e) =>
                          updateApproval(index, "approverName", e.target.value)
                        }
                      />
                      <Input
                        placeholder="Approver Role"
                        value={approval.approverRole}
                        onChange={(e) =>
                          updateApproval(index, "approverRole", e.target.value)
                        }
                      />
                      <IconButton
                        icon={<DeleteIcon />}
                        colorScheme="red"
                        variant="outline"
                        onClick={() => removeApproval(index)}
                      />
                    </HStack>
                  ))}
                  <Button
                    leftIcon={<AddIcon />}
                    variant="outline"
                    onClick={addApproval}
                    alignSelf="flex-start"
                  >
                    Add Approver
                  </Button>
                </VStack>
              </Box>

              <Divider />

              {/* Submit Button */}
              <Flex justify="flex-end" gap={4}>
                <Button variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorScheme="blue"
                  isLoading={loading}
                  loadingText="Creating Offer..."
                >
                  Create Offer
                </Button>
              </Flex>
            </VStack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CreateOfferModal;
